import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import { api } from "./convex/_generated/api";

import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

import "./PhysicalHealthPage.css";

export default function PhysicalHealthPage() {
  const { progressId, userId } = useParams();

  const [exerciseMinutes, setExerciseMinutes] = useState("");
  const [waterGlasses, setWaterGlasses] = useState("");

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  /*
   * ================================
   * MUTATION
   * ================================
   *
   * Saves today's daily record
   * AND calculates/updates the weekly score.
   */

  const calculatePhysicalHealth = useMutation(
    api.progress.calculatePhysicalHealth
  );

  /*
   * ================================
   * USUAL STORED RESULTS
   * ================================
   *
   * Gets all previously stored:
   *
   * - physicalHealthDaily records
   * - physicalHealthWeekly records
   *
   * Charts below use these records.
   *
   * This query is reactive in Convex.
   * After the mutation saves new data,
   * this query automatically updates.
   */

  const usualresult = useQuery(
    api.progress.getPhysicalHealthResults,
    progressId && userId
      ? {
          progressId,
          userId,
        }
      : "skip"
  );

  console.log(
    "PHYSICAL HEALTH USUAL RESULT:",
    usualresult
  );

  /*
   * ================================
   * GET STORED RECORDS
   * ================================
   */

  const dailyRecords =
    usualresult?.dailyRecords ?? [];

  const weeklyRecords =
    usualresult?.weeklyRecords ?? [];

  /*
   * ================================
   * SORT DAILY RECORDS
   * ================================
   */

  const sortedDailyRecords = [
    ...dailyRecords,
  ].sort((a, b) =>
    a.date.localeCompare(b.date)
  );

  /*
   * ================================
   * SORT WEEKLY RECORDS
   * ================================
   */

  const sortedWeeklyRecords = [
    ...weeklyRecords,
  ].sort((a, b) =>
    a.weekStart.localeCompare(b.weekStart)
  );

  /*
   * ================================
   * LATEST WEEKLY RECORD
   * ================================
   */

  const latestWeeklyRecord =
    sortedWeeklyRecords.length > 0
      ? sortedWeeklyRecords[
          sortedWeeklyRecords.length - 1
        ]
      : null;

  /*
   * ================================
   * DAILY SCORE CHART
   * ================================
   *
   * Uses STORED daily records.
   * Does NOT use form state.
   */

  const dailyScoreChartData =
    sortedDailyRecords.map((record) => ({
      date: record.date,
      dailyScore: Math.round(
        Number(record.dailyScore ?? 0)
      ),
    }));

  console.log(
    "PHYSICAL DAILY RECORDS:",
    dailyRecords
  );

  console.log(
    "PHYSICAL DAILY CHART DATA:",
    dailyScoreChartData
  );

  /*
   * ================================
   * WEEKLY SCORE CHART
   * ================================
   *
   * Uses STORED weekly records.
   */

  const weeklyScoreChartData =
    sortedWeeklyRecords.map((record) => ({
      week: record.weekStart,

      healthScore: Math.round(
        Number(record.healthScore ?? 0)
      ),

      activityScore: Math.round(
        Number(record.activityScore ?? 0)
      ),

      finalScore: Math.round(
        Number(record.finalScore ?? 0)
      ),
    }));

  console.log(
    "PHYSICAL WEEKLY RECORDS:",
    weeklyRecords
  );

  console.log(
    "PHYSICAL WEEKLY CHART DATA:",
    weeklyScoreChartData
  );

  /*
   * ================================
   * GET CURRENT WEEK
   * ================================
   */

  const getCurrentWeek = () => {
    const today = new Date();

    const day = today.getDay();

    // Sunday = 0
    // Monday = 1

    const daysSinceMonday =
      day === 0 ? 6 : day - 1;

    const monday = new Date(today);

    monday.setDate(
      today.getDate() - daysSinceMonday
    );

    const sunday = new Date(monday);

    sunday.setDate(
      monday.getDate() + 6
    );

    const weekStart = monday
      .toISOString()
      .split("T")[0];

    const weekEnd = sunday
      .toISOString()
      .split("T")[0];

    return {
      weekStart,
      weekEnd,
    };
  };

  /*
   * ================================
   * GET TODAY
   * ================================
   */

  const getToday = () => {
    const today = new Date();

    return today
      .toISOString()
      .split("T")[0];
  };

  /*
   * ================================
   * HANDLE SUBMIT
   * ================================
   */

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");
    setResult(null);

    if (!progressId || !userId) {
      setError(
        "Missing progress or user information."
      );
      return;
    }

    if (
      exerciseMinutes === "" ||
      waterGlasses === ""
    ) {
      setError(
        "Please enter your exercise and water intake."
      );
      return;
    }

    try {
      setLoading(true);

      /*
       * Convert inputs to numbers
       */

      const exercise =
        Number(exerciseMinutes);

      const water =
        Number(waterGlasses);

      /*
       * Validate numbers
       */

      if (
        Number.isNaN(exercise) ||
        Number.isNaN(water) ||
        exercise < 0 ||
        water < 0
      ) {
        setError(
          "Please enter valid numbers."
        );
        return;
      }

      /*
       * ================================
       * GET TODAY
       * ================================
       */

      const date = getToday();

      /*
       * ================================
       * GET CURRENT WEEK
       * ================================
       */

      const {
        weekStart,
        weekEnd,
      } = getCurrentWeek();

      /*
       * ================================
       * SAVE DAILY + CALCULATE WEEKLY
       * ================================
       */

      const weeklyResult =
        await calculatePhysicalHealth({
          userId,
          progressId,

          // Daily data
          date,
          exerciseMinutes: exercise,
          waterGlasses: water,

          // Weekly data
          weekStart,
          weekEnd,
        });

      console.log(
        "Physical Health Result:",
        weeklyResult
      );

      /*
       * Keep mutation result only for
       * immediate result display.
       *
       * Charts do NOT use this.
       */

      setResult({
        ...weeklyResult,
        date,
      });

    } catch (err) {
      console.error(
        "Physical Health Error:",
        err
      );

      setError(
        err?.message ||
          "Failed to save physical health."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="physical-health-page">
      <div className="physical-health-container">

        {/* =========================
            PAGE HEADER
        ========================= */}

        <header className="physical-health-header">

          <span className="physical-health-eyebrow">
            PHYSICAL WELLNESS
          </span>

          <h1>
            Physical Health
          </h1>

          <p>
            Track your daily exercise and
            hydration.
          </p>

        </header>

        {/* =========================
            INPUT FORM
        ========================= */}

        <form
          onSubmit={handleSubmit}
          className="physical-health-form"
        >

          <div className="form-header">
            <div className="form-icon">
              +
            </div>

            <div>
              <h2>
                Daily Health Tracking
              </h2>

              <p>
                Record your exercise and
                water intake for today.
              </p>
            </div>
          </div>

          {/* Exercise */}

          <div className="input-group">

            <label>
              How many minutes did you exercise
              today?
            </label>

            <input
              type="number"
              min="0"
              value={exerciseMinutes}
              onChange={(e) =>
                setExerciseMinutes(
                  e.target.value
                )
              }
              placeholder="Example: 30"
            />

          </div>

          {/* Water */}

          <div className="input-group">

            <label>
              How many glasses of water did you
              drink today?
            </label>

            <input
              type="number"
              min="0"
              value={waterGlasses}
              onChange={(e) =>
                setWaterGlasses(
                  e.target.value
                )
              }
              placeholder="Example: 8"
            />

          </div>

          {/* Submit */}

          <button
            type="submit"
            disabled={loading}
            className="physical-health-submit"
          >
            {loading
              ? "Saving..."
              : "Save Physical Health"}
          </button>

        </form>

        {/* =========================
            ERROR
        ========================= */}

        {error && (
          <div className="physical-health-error">
            <span>!</span>
            {error}
          </div>
        )}

        {/* =========================
            STORED RESULTS COUNTS
        ========================= */}

        <div className="record-counts">

          <div className="record-count">
            <span>
              DAILY RECORDS
            </span>

            <strong>
              {dailyRecords.length}
            </strong>
          </div>

          <div className="record-count">
            <span>
              WEEKLY RECORDS
            </span>

            <strong>
              {weeklyRecords.length}
            </strong>
          </div>

        </div>

        {/* =========================
            IMMEDIATE RESULT
        ========================= */}

        {result && (
          <div className="results-section">

            {/* TODAY'S SCORE */}

            <div className="content-card today-score-card">

              <div className="content-header">
                <span>
                  TODAY'S PERFORMANCE
                </span>

                <h2>
                  Today's Score
                </h2>
              </div>

              <div className="today-score-value">
                {Math.round(
                  result.dailyScore
                )}

                <span>
                  /100
                </span>
              </div>

              <p className="score-date">
                {result.date}
              </p>

            </div>

            {/* CURRENT WEEK SCORE CARDS */}

            <div className="score-section">

              <div className="section-heading">
                <span>
                  WEEKLY PERFORMANCE
                </span>

                <h2>
                  Physical Health Score
                </h2>
              </div>

              <div className="score-grid">

                {/* Health Tracking */}

                <div className="score-card green-card">

                  <span className="score-label">
                    TRACKING
                  </span>

                  <h3>
                    Health Tracking
                  </h3>

                  <p>
                    {Math.round(
                      result.healthScore
                    )}
                  </p>

                </div>

                {/* Activity Score */}

                <div className="score-card lime-card">

                  <span className="score-label">
                    ACTIVITY
                  </span>

                  <h3>
                    Activity Score
                  </h3>

                  <p>
                    {Math.round(
                      result.activityScore
                    )}
                  </p>

                </div>

                {/* Final Score */}

                <div className="score-card orange-card">

                  <span className="score-label">
                    OVERALL
                  </span>

                  <h3>
                    Final Score
                  </h3>

                  <p>
                    {Math.round(
                      result.finalScore
                    )}
                  </p>

                </div>

              </div>

            </div>

          </div>
        )}

        {/* ==================================================
            STORED DAILY RESULTS
            ================================================== */}

        {dailyScoreChartData.length > 0 && (
          <div className="content-card chart-card">

            <div className="chart-header">

              <div>
                <span>
                  DAILY PERFORMANCE
                </span>

                <h2>
                  Daily Physical Health Scores
                </h2>
              </div>

              <div className="chart-badge">
                0 — 100
              </div>

            </div>

            <div className="chart-container">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={
                    dailyScoreChartData
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="date"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[
                      0,
                      100,
                    ]}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#101713",
                      border:
                        "1px solid #35503e",
                      borderRadius:
                        "12px",
                      color:
                        "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="dailyScore"
                    name="Daily Score"
                    fill="#22c55e"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>
        )}

        {/* ==================================================
            STORED WEEKLY RESULTS
            ================================================== */}

        {weeklyScoreChartData.length > 0 && (
          <div className="content-card chart-card">

            <div className="chart-header">

              <div>
                <span>
                  WEEKLY PERFORMANCE
                </span>

                <h2>
                  Weekly Physical Health Scores
                </h2>
              </div>

              <div className="chart-badge">
                0 — 100
              </div>

            </div>

            <div className="chart-container">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={
                    weeklyScoreChartData
                  }
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="week"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    domain={[
                      0,
                      100,
                    ]}
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#101713",
                      border:
                        "1px solid #35503e",
                      borderRadius:
                        "12px",
                      color:
                        "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="healthScore"
                    name="Health Tracking"
                    fill="#22c55e"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="activityScore"
                    name="Activity Score"
                    fill="#84cc16"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />

                  <Bar
                    dataKey="finalScore"
                    name="Final Score"
                    fill="#f59e0b"
                    radius={[
                      6,
                      6,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>
        )}

        {/* ==================================================
            WEEKLY ACTIVITY SUMMARY
            ================================================== */}

        {latestWeeklyRecord && (
          <div className="content-card chart-card">

            <div className="chart-header">

              <div>
                <span>
                  WEEKLY ACTIVITY
                </span>

                <h2>
                  Weekly Activity Summary
                </h2>
              </div>

              <div className="chart-badge">
                COUNTS
              </div>

            </div>

            <div className="chart-container chart-container-small">

              <ResponsiveContainer
                width="100%"
                height="100%"
              >

                <BarChart
                  data={[
                    {
                      name: "Days",
                      value: Number(
                        latestWeeklyRecord.daysMeasured ??
                          0
                      ),
                    },
                    {
                      name: "Suggestions",
                      value: Number(
                        latestWeeklyRecord.suggestionCount ??
                          0
                      ),
                    },
                  ]}
                >

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#26372d"
                  />

                  <XAxis
                    dataKey="name"
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={{
                      stroke: "#33443a",
                    }}
                    tickLine={false}
                  />

                  <YAxis
                    tick={{
                      fill: "#94a3b8",
                      fontSize: 12,
                    }}
                    axisLine={false}
                    tickLine={false}
                  />

                  <Tooltip
                    contentStyle={{
                      background:
                        "#101713",
                      border:
                        "1px solid #35503e",
                      borderRadius:
                        "12px",
                      color:
                        "#ffffff",
                    }}
                  />

                  <Legend />

                  <Bar
                    dataKey="value"
                    name="Count"
                    fill="#22c55e"
                    radius={[
                      7,
                      7,
                      0,
                      0,
                    ]}
                  />

                </BarChart>

              </ResponsiveContainer>

            </div>

          </div>
        )}

        {/* ==================================================
            WEEKLY DETAILS
            ================================================== */}

        {latestWeeklyRecord && (
          <div className="content-card weekly-details-card">

            <div className="content-header">

              <div>
                <span>
                  WEEKLY BREAKDOWN
                </span>

                <h2>
                  Latest Weekly Details
                </h2>
              </div>

            </div>

            <div className="details-grid">

              <p>
                <strong>
                  Week start:
                </strong>{" "}
                {
                  latestWeeklyRecord.weekStart
                }
              </p>

              <p>
                <strong>
                  Week end:
                </strong>{" "}
                {
                  latestWeeklyRecord.weekEnd
                }
              </p>

              <p>
                <strong>
                  Days measured:
                </strong>{" "}
                {
                  latestWeeklyRecord.daysMeasured
                }
              </p>

              <p>
                <strong>
                  Suggestions:
                </strong>{" "}
                {
                  latestWeeklyRecord.suggestionCount ?? 0
                }
              </p>

              <p>
                <strong>
                  Health tracking score:
                </strong>{" "}
                {Math.round(
                  Number(
                    latestWeeklyRecord.healthScore ?? 0
                  )
                )}
              </p>

              <p>
                <strong>
                  Activity score:
                </strong>{" "}
                {Math.round(
                  Number(
                    latestWeeklyRecord.activityScore ?? 0
                  )
                )}
              </p>

              <p>
                <strong>
                  Final score:
                </strong>{" "}
                {Math.round(
                  Number(
                    latestWeeklyRecord.finalScore ?? 0
                  )
                )}
              </p>

            </div>

          </div>
        )}

      </div>
    </div>
  );
}
